// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { type ApiErrorPayloadCommon, isApiError } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatform } from '../../context/platform/hooks';
import { apiClient } from '../client';

const SendOtpTokenRequest = async (phoneNumber: string, codingContext: CodingContext | undefined): Promise<string> => {
  const request = {
    phoneNumber: codingContext?.encryptString(phoneNumber),
  };
  const response = await apiClient.post<{ id: string }>('otp/send', () => request, codingContext);
  return response.id;
};

export const useSendOtpTokenMutation = (options?: { onSuccess?: (id: string) => void }) => {
  const { codingContext } = usePlatform();
  const [apiError, setApiError] = useState<ApiErrorPayloadCommon | undefined>(undefined);
  const mutation = useMutation<string, Error, string>({
    mutationFn: (phoneNumber) => SendOtpTokenRequest(phoneNumber, codingContext),
    onSuccess: (id: string) => {
      options?.onSuccess?.(id);
    },
    onError(error) {
      if (isApiError(error)) {
        setApiError(error);
      } else {
        setApiError({ error: { code: 'unknown', message: 'Unknown error' } });
      }
    },
  });

  const resetApiError = () => {
    setApiError(undefined);
  };

  return {
    sendOtpToken: mutation.mutate,
    apiError,
    resetApiError,
    ...mutation,
  };
};
